
import { Consultancy, RFQ, Region, TherapeuticArea, RegulatoryPathway, MatchResult } from '../types';
import { MOCK_CONSULTANCIES } from '../constants';

// Weights for matching algorithm
const WEIGHTS = {
  REGION: 25,
  THERAPEUTIC_AREA: 30,
  PATHWAY: 25,
  PRODUCT_TYPE: 20
};

export const calculateMatchScore = (consultancy: Consultancy, criteria: Partial<RFQ>): MatchResult => {
  let score = 0;
  const reasons: string[] = [];

  // 1. Region Match (25%)
  if (criteria.regions && criteria.regions.some(r => consultancy.regions.includes(r))) {
    // Check for exact overlap ratio
    const overlap = criteria.regions.filter(r => consultancy.regions.includes(r)).length;
    const ratio = overlap / criteria.regions.length;
    const points = Math.round(WEIGHTS.REGION * ratio);
    score += points;
    if (points > 15) reasons.push(`Strong regional match (${overlap} authorities)`);
  }

  // 2. Therapeutic Area Match (30%)
  if (criteria.therapeuticArea && consultancy.therapeuticAreas.includes(criteria.therapeuticArea)) {
    score += WEIGHTS.THERAPEUTIC_AREA;
    reasons.push(`Specialist in ${criteria.therapeuticArea}`);
  }

  // 3. Pathway Match (25%)
  if (criteria.pathway && consultancy.pathways.includes(criteria.pathway)) {
    score += WEIGHTS.PATHWAY;
    reasons.push(`Expertise in ${criteria.pathway}`);
  } else if (criteria.pathway) {
      // Partial credit if they have related general expertise (mock logic)
      // e.g., having Orphan designation when requesting Orphan
      if (criteria.pathway === RegulatoryPathway.ORPHAN && consultancy.orphanDesignations > 0) {
          score += 15;
          reasons.push('Proven Orphan Drug track record');
      }
  }

  // 4. Bonus: Verified Approvals
  if (consultancy.verifiedApprovals > 10) {
      score += 5;
      reasons.push('High volume of verified approvals');
  }

  return {
    consultancyId: consultancy.id,
    score: Math.min(score, 100), // Cap at 100
    reasons
  };
};

export const getSuggestedConsultancies = (criteria: Partial<RFQ>): { consultancy: Consultancy; match: MatchResult }[] => {
  const results = MOCK_CONSULTANCIES.map(consultancy => ({
    consultancy,
    match: calculateMatchScore(consultancy, criteria)
  }));

  // Sort by score descending
  return results
    .sort((a, b) => b.match.score - a.match.score)
    .filter(r => r.match.score > 20); // Filter out very low matches
};

export const generateMockSLAMetrics = (rfq: RFQ) => {
    // Simulates backend calculation for dashboard
    const now = new Date();
    const posted = new Date(rfq.postedDate);
    const hoursDiff = Math.abs(now.getTime() - posted.getTime()) / 36e5;

    return {
        timeToFirstView: Math.max(0.5, Math.round(hoursDiff * 0.1 * 10) / 10), // Mock data
        avgResponseTime: 24,
        responseRate: rfq.proposalCount > 0 ? 100 : 0
    };
};
